'use client';

import { Button, Chip } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useState } from 'react';
import type { ReportSubmitProps } from './props';

export function ReportSubmit({ paths, isReadOnly, loadingPathId, onSubmit }: ReportSubmitProps) {
    const [format, setFormat] = useState<'pdf' | 'pptx'>('pdf');
    const isAnyLoading = !!loadingPathId;

    if (!paths.length) return null;

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
                <p className="text-tiny font-medium text-default-500">Output format</p>
                <div className="flex overflow-hidden rounded-lg border border-default-200">
                    {(['pdf', 'pptx'] as const).map((f) => (
                        <button
                            key={f}
                            type="button"
                            disabled={isAnyLoading || isReadOnly}
                            onClick={() => setFormat(f)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-tiny font-medium transition-colors ${
                                format === f
                                    ? 'bg-mfour-green text-white'
                                    : 'bg-transparent text-default-500 hover:bg-default-100'
                            }`}
                        >
                            <Icon
                                icon={f === 'pdf' ? 'lucide:file-text' : 'lucide:presentation'}
                                width={12}
                            />
                            {f === 'pdf' ? 'PDF' : 'PowerPoint'}
                        </button>
                    ))}
                </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
                {paths.map((path) => {
                    const isThisLoading = loadingPathId === path.id;
                    return (
                        <div
                            key={path.id}
                            className={`flex flex-col justify-between rounded-xl border p-5 transition-colors ${
                                isReadOnly || isAnyLoading
                                    ? 'opacity-60'
                                    : path.recommended
                                      ? 'border-mfour-green/30 bg-mfour-green/5'
                                      : 'border-default-200'
                            }`}
                        >
                            <div>
                                <div className="flex flex-wrap items-center gap-1.5">
                                    <p className="text-small font-semibold text-foreground">
                                        {path.label}
                                    </p>
                                    {path.recommended && (
                                        <Chip
                                            size="sm"
                                            variant="flat"
                                            classNames={{
                                                base: 'h-5 bg-mfour-green/15 px-2 shrink-0',
                                                content:
                                                    'text-mfour-green text-tiny font-medium px-0',
                                            }}
                                        >
                                            Recommended
                                        </Chip>
                                    )}
                                </div>
                                <p className="mt-2 text-tiny leading-relaxed text-default-400">
                                    {path.description}
                                </p>
                            </div>
                            <Button
                                size="sm"
                                className={`mt-5 w-full font-medium ${
                                    path.recommended
                                        ? 'bg-mfour-green text-white data-[hover=true]:bg-mfour-green/90'
                                        : 'border-default-300 text-default-600 data-[hover=true]:border-default-400'
                                }`}
                                variant={path.recommended ? 'solid' : 'bordered'}
                                isDisabled={isReadOnly || isAnyLoading}
                                isLoading={isThisLoading}
                                onPress={() => onSubmit(path.id, format)}
                                endContent={
                                    !isThisLoading ? (
                                        <Icon icon="lucide:arrow-right" width={14} />
                                    ) : undefined
                                }
                            >
                                {isThisLoading ? 'Generating...' : path.cta}
                            </Button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
